import { StatusBadge } from "../../../shared/components/badges/StatusBadge";
import { UserGroupIcon } from "@heroicons/react/24/outline";
import { ProfileUpload } from "../components/profileUpload";
import { UserService } from "../../../shared/services/userService";
import { RoleService } from "../../../shared/services/roleService";
import { CenterService } from "../../../shared/services/centerService";

const loadDropdownOptions = async (apiCall, key) => {
  try {
    const res = await apiCall();

    const data = res?.data?.[key] || res?.data?.data?.[key] || res?.data || [];

    return data.map((item) => ({
      value: item.value || item.id || item.name,
      label: item.label || item.name,
    }));
  } catch (error) {
    console.warn(`Failed to load dropdown options for ${key}`, error);
    return [];
  }
};

const loadCenterOptions = async () => {
  try {
    const res = await CenterService.getAll({ limit: 100 });
    const centers = res?.data?.vaccinationCenters || res?.data || [];
    return centers.map((c) => ({ value: c.id, label: c.name }));
  } catch {
    return [];
  }
};

const loadRoleOptions = async () => {
  try {
    const res = await RoleService.getAll({ limit: 100 });
    const roles = res?.data?.data || [];
    return roles.map((r) => ({ value: r.id, label: r.name }));
  } catch {
    return [];
  }
};

// status options for form field
export const getUserStatusOptions = () =>
  loadDropdownOptions(UserService.AllDropdowns, "userStatuses");

// status options for filter dropdown (excludes "ALL" option as it's added automatically)
export const getUserFilterStatusOptions = async () => {
  try {
    const res = await UserService.AllDropdowns();
    const statuses =
      res?.data?.userStatuses || res?.data?.data?.userStatuses || [];

    // Filter out "ALL" as the filter dropdown adds its own "All Statuses" option
    return statuses
      .filter((item) => item.value !== "ALL")
      .map((item) => ({
        value: item.value,
        label: item.label,
      }));
  } catch (error) {
    console.warn("Failed to load user status options for filter", error);
    return [
      { value: "ACTIVE", label: "Active" },
      { value: "INACTIVE", label: "Inactive" },
      { value: "SUSPENDED", label: "Suspended" },
      { value: "DELETED", label: "Deleted" },
    ];
  }
};

export const userConfig = {
  title: "User",
  endpoint: "admin/users",
  storageKey: "users",
  icon: UserGroupIcon,
  useUrlNavigation: true,
  basePath: "/admin/User",

  // Status filter options loader - fetches from /api/v1/dropdowns
  statusOptionsLoader: getUserFilterStatusOptions,

  // Columns for table/grid
  columns: [
    {
      key: "fullName",
      label: "User",
      render: (item) => {
        const imageSrc = item.profilePicture;
        // console.log('User profilePicture:', imageSrc);
        return (
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 h-10 w-10">
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={item.fullName || "User"}
                  className="h-10 w-10 rounded-full object-cover"
                  onError={(e) => {
                    console.error("Image load failed:", imageSrc);
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                  // onLoad={() => console.log('Image loaded successfully:', imageSrc)}
                />
              ) : null}
              <div
                className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center"
                style={{ display: imageSrc ? "none" : "flex" }}
              >
                <span className="text-lg">👤</span>
              </div>
            </div>
            <div>{item.fullName || "—"}</div>
          </div>
        );
      },
    },

    { key: "email", label: "Contact", render: (item) => item.email },
    { key: "phone", label: "Phone", render: (item) => item.phone },
    {
      key: "assignedRoles",
      label: "Role",
      render: (item) => item.assignedRoles?.length
        ? <div className="flex flex-wrap gap-1">{item.assignedRoles.map(r => <span key={r.id} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">{r.name}</span>)}</div>
        : <span className="text-gray-400 text-xs">No role</span>,
    },
    {
      key: "status",
      label: "Status",
      render: (item) => <StatusBadge status={item.status.toLowerCase()} />,
    },
  ],

  // Form fields
  formFields: [
    {
      name: "profilePicture",
      label: "Profile Picture",
      accept: "image/*",
      placeholder: "Upload profile picture (optional)",
      fullWidth: true,
      uploadApi: UserService.uploadProfilePhoto,
      customRender: (field, value, onChange, error) => (
        <ProfileUpload
          field={field}
          value={value}
          onChange={onChange}
          error={error}
        />
      ),
    },
    {
      name: "fullName",
      label: "Full Name",
      type: "text",
      required: true,
      placeholder: "Enter full name",
    },
    {
      name: "email",
      label: "Email",
      type: "email",
      required: true,
      placeholder: "Enter email",
    },
    {
      name: "phone",
      label: "phone",
      type: "phone",
      required: true,
      placeholder: "Enter phone number",
    },
    {
      name: "password",
      label: "Password",
      type: "password",
      required: true,
      placeholder: "Enter password",
      showOnlyInModes: ["add"],
    },
    {
      name: "password",
      label: "New Password",
      type: "password",
      required: false,
      placeholder: "Leave blank to keep current password",
      showOnlyInModes: ["edit"],
    },
    {
      name: "roleIds",
      label: "Assign Roles",
      type: "multi-select",
      required: false,
      optionsLoader: loadRoleOptions,
      placeholder: "Select roles (optional)",
    },
    {
      name: "centerIds",
      label: "Assign Centers",
      type: "multi-select",
      required: false,
      optionsLoader: loadCenterOptions,
      placeholder: "Select centers (optional)",
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      required: true,
      optionsLoader: getUserStatusOptions,
      placeholder: "Select status",
    },
  ],

  searchFields: ["name", "email", "role", "status"],

  customHandlers: {
    processFormData: (data, mode) => {
      const processed = { ...data, email: data.email?.toLowerCase() };
      if (mode === 'edit' && !processed.password) {
        delete processed.password;
      }
      // Ensure arrays (never undefined)
      processed.roleIds = processed.roleIds || [];
      processed.centerIds = processed.centerIds || [];
      return processed;
    },
    // renderExpandedRow: (item) => <UserExpandedRow item={item} />
  },
};
